import React, { useEffect, useRef, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import { IStore } from "redux/store";
import YearPicker from "../../individualreport/reportType/garbage/_elements/YearPicker";
import { getYearOptions } from "redux/app/report/utils";
import VesselSelector from "../../common/VesselSelector";
import classNames from "classnames";
import { vesselActions } from "redux/app/vessel";
import reportActions from "redux/app/report/actions";
import reportSelectors from "redux/app/report/selectors";
import useElementSize from "hooks/useElementSize";
import PackagingGraph from "./_elements/PackagingGraph";
import PackagingTable from "./_elements/PackagingTable";
import PackagingCustomSearch from "./_elements/PackagingCustomSearch";

type ViewMode = "graph" | "table" | "search";

type OwnProps = {};
type PackagingProps = PropsFromRedux & OwnProps;
function Packaging(props: PackagingProps) {
	const {
		jobToGetVesselSpecificPackagingReport,
		jobToLoadAllVesselAggregatePackagingReport,
	} = props;
	const reportContainerRef = useRef<HTMLDivElement>(null);
	const [viewMode, setViewMode] = useState<ViewMode>("graph");
	const { width: reportContainerWidth } = useElementSize(reportContainerRef);
	const [year, setYear] = useState(new Date().getFullYear().toString());
	const [vesselId, setVesselId] = useState("");

	useEffect(() => {
		props.loadVessels();
		if (!vesselId) {
			props.loadPackagingAllVesselsAggregateData(year);
		}
	}, [vesselId]);

	const loadData = (selVesselId: string, selYear: string) => {
		setVesselId(selVesselId);
		setYear(selYear);
		if (selVesselId) {
			props.loadPackagingVesselSpecificAggregateData(selVesselId, selYear);
		} else {
			props.loadPackagingAllVesselsAggregateData(selYear);
		}
	};

	return (
		<div
			ref={reportContainerRef}
			className="bg-white m-2 rounded-lg h-[calc(100vh-98px)] overflow-y-auto"
		>
			<div className="">
				<div className="px-5 pb-0 py-8">
					<div className="flex items-center">
						<span
							title="go back"
							onClick={() => {
								window?.history?.back();
							}}
							className="bp3-icon bp3-icon-arrow-left text-lg mr-3 cursor-pointer"
						/>
						<h2 className="text-xl font-medium">Packaging</h2>
					</div>
					<div className="flex items-center justify-between">
						<div className="flex items-center">
							<div className="flex  items-baseline">
								{viewMode !== "search" && (
									<>
										<div className="ml-4 text-base">
											<YearPicker
												options={getYearOptions().map((op) => ({
													label: op.toString(),
													value: op.toString(),
												}))}
												value={year}
												id="emissions-year-picker"
												onSelect={(option) => {
													loadData(vesselId, option?.value ?? "");
												}}
												isLoading={
													jobToGetVesselSpecificPackagingReport.isLoading ||
													jobToLoadAllVesselAggregatePackagingReport.isLoading
												}
											/>
										</div>
										<div className="pl-4 min-w-28 md:w-56">
											<VesselSelector
												onVesselSelect={(vesselId: string) => {
													loadData(vesselId, year);
												}}
												isLoading={
													jobToGetVesselSpecificPackagingReport.isLoading ||
													jobToLoadAllVesselAggregatePackagingReport.isLoading
												}
												selectedVesselId={vesselId}
												showDropdownLabel={false}
											/>
										</div>
									</>
								)}
							</div>
						</div>
						<div>
							<div className="p-0">
								<div className="border-gray-200 ">
									<div className="flex flex-row justify-around py-1 my-2 rounded-md bg-lightGray">
										<button
											className={classNames(
												"transition-all duration-300 px-10 text-[12px]",
												{
													"p-2 flex-1 mx-1 font-medium text-gray-800 bg-white rounded-md shadow-sm outline-none":
														viewMode === "graph",
													"p-2 flex-1 mx-1 font-medium text-gray-500 rounded-md outline-none hover:text-gray-700":
														viewMode !== "graph",
												},
											)}
											onClick={() => setViewMode("graph")}
										>
											Graph
										</button>
										<button
											className={classNames(
												"transition-all duration-300 px-10 text-[12px]",
												{
													"py-1.5 flex-1 mx-1 font-medium text-gray-800 bg-white rounded-md shadow-sm outline-none":
														viewMode === "table",
													"py-1.5 flex-1 mx-1 font-medium text-gray-500 rounded-md outline-none hover:text-gray-700":
														viewMode !== "table",
												},
											)}
											onClick={() => setViewMode("table")}
										>
											Table
										</button>
										<button
											className={classNames(
												"transition-all duration-300 px-10 text-[12px]",
												{
													"py-1.5 flex-1 mx-1 font-medium text-gray-800 bg-white rounded-md shadow-sm outline-none":
														viewMode === "search",
													"py-1.5 flex-1 mx-1 font-medium text-gray-500 rounded-md outline-none hover:text-gray-700":
														viewMode !== "search",
												},
											)}
											onClick={() => setViewMode("search")}
										>
											Search
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{viewMode === "search" ? (
				<PackagingCustomSearch />
			) : viewMode === "graph" ? (
				<PackagingGraph
					reportContainerWidth={reportContainerWidth}
					vesselId={vesselId}
					year={year}
				/>
			) : (
				<PackagingTable vesselId={vesselId} year={year} />
			)}
		</div>
	);
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
	const reportStore = reportSelectors._getStore(store);
	const jobToGetVesselSpecificPackagingReport =
		reportSelectors.jobToLoadVesselSpecificPackagingAggregate(reportStore);
	const jobToLoadAllVesselAggregatePackagingReport =
		reportSelectors.jobToLoadAllVesselsPackagingAggregate(reportStore);
	return {
		jobToGetVesselSpecificPackagingReport,
		jobToLoadAllVesselAggregatePackagingReport,
	};
}
function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {
		loadVessels() {
			dispatch(vesselActions.listLoad(true));
		},
		loadPackagingAllVesselsAggregateData(year: string) {
			dispatch(
				reportActions.commands.packagingAllVesselAggregateDataLoad(year),
			);
		},
		loadPackagingVesselSpecificAggregateData(vesselId: string, year: string) {
			dispatch(
				reportActions.commands.packagingVesselSpecificAggregateDataLoad(
					vesselId,
					year,
				),
			);
		},
	};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(Packaging);
